import { AgTechQuery, AgTechQueryBuilder, AgTechQueryResponses } from "@agtech/core/data/queries/AgTechDataQueries";
import { useReportDataGetAfterSaleReportRequest, useReportDataGetOutstandingCreditsReportRequest, useReportDataGetLotOrderReportRequest, useReportDataGetLotsByPurchaserReportRequest, useReportDataGetLotsPurchasedInOrderReportRequest, useReportDateGetUnpaidInvoicesRequest } from "./ReportDataActions";
import { SaleReport } from "./ReportData";
import { useCurrentSale } from "../sales/SaleDataStore";

export const ReportDataGetSaleReportsQuery: AgTechQuery<any, SaleReport[]> = {
    queryKey: () => ['Reports'],
    query: async () => {
        return AgTechQueryResponses.Success<SaleReport[]>([]);
    },
    content: {
        loadingText: 'Loading Reports...',
        failureText: 'Failed to load reports'
    }
}

export const useReportDataGetSaleReportsQuery = () => {
    const currentSale = useCurrentSale();
    const afterSaleReportRequest = useReportDataGetAfterSaleReportRequest();
    const outstandingCreditsReportRequest = useReportDataGetOutstandingCreditsReportRequest();
    const lotOrderReportRequest = useReportDataGetLotOrderReportRequest();
    const lotDetailReportRequest = useReportDataGetLotsByPurchaserReportRequest();
    const lotPurchaseOrderReportRequest = useReportDataGetLotsPurchasedInOrderReportRequest();
    const unpaidInvoicesReportRequest = useReportDateGetUnpaidInvoicesRequest();

    return AgTechQueryBuilder.buildQueryResult({
        data: ([
            {
                reportName: 'After Sale Report',
                reportDescription: 'The after sale report for viewing after the sale.',
                reportDownloadAction: async () => afterSaleReportRequest.execute({
                    data: { saleId: currentSale.id, format: 'Pdf' }
                }),
                reportDownloadFormats: [
                    {
                        label: 'PDF',
                        icon: 'fas fa-file-pdf',
                        action: async () => afterSaleReportRequest.execute({ data: { saleId: currentSale.id, format: 'Pdf' } })
                    },
                    {
                        label: 'Excel',
                        icon: 'fas fa-file-excel',
                        action: async () => afterSaleReportRequest.execute({ data: { saleId: currentSale.id, format: 'Excel' } })
                    },
                    {
                        label: 'CSV',
                        icon: 'fas fa-file-csv',
                        action: async () => afterSaleReportRequest.execute({ data: { saleId: currentSale.id, format: 'Csv' } })
                    }
                ],
                reportIcon: 'fas fa-chart-area',
                reportCategory: 'Sale'
            },
            {
                reportName: 'Lot Order Report',
                reportDescription: 'Report shows lot order for handing out to bidders.',
                reportDownloadAction: async () => lotOrderReportRequest.execute({
                    data: currentSale.id
                }),
                reportIcon: 'fas fa-list',
                reportCategory: 'Lots'
            },
            {
                reportName: 'Lot Details by Purchaser Report',
                reportDescription: 'This report contains lot sale details ordered by lot purchaser.',
                reportDownloadAction: async () => lotDetailReportRequest.execute({
                    data: { saleId: currentSale.id, format: 'Pdf' }
                }),
                reportDownloadFormats: [
                    {
                        label: 'PDF',
                        icon: 'fas fa-file-pdf',
                        action: async () => lotDetailReportRequest.execute({ data: { saleId: currentSale.id, format: 'Pdf' } })
                    },
                    {
                        label: 'Excel',
                        icon: 'fas fa-file-excel',
                        action: async () => lotDetailReportRequest.execute({ data: { saleId: currentSale.id, format: 'Excel' } })
                    },
                    {
                        label: 'CSV',
                        icon: 'fas fa-file-csv',
                        action: async () => lotDetailReportRequest.execute({ data: { saleId: currentSale.id, format: 'Csv' } })
                    }
                ],
                reportIcon: 'fas fa-list',
                reportCategory: 'Lots'
            },
            {
                reportName: 'Lot Details by Lot Number Report',
                reportDescription: 'This report contains lot sale details ordered by lot number.',
                reportDownloadAction: async () => lotPurchaseOrderReportRequest.execute({
                    data: { saleId: currentSale.id, format: 'Pdf' }
                }),
                reportDownloadFormats: [
                    {
                        label: 'PDF',
                        icon: 'fas fa-file-pdf',
                        action: async () => lotPurchaseOrderReportRequest.execute({ data: { saleId: currentSale.id, format: 'Pdf' } })
                    },
                    {
                        label: 'Excel',
                        icon: 'fas fa-file-excel',
                        action: async () => lotPurchaseOrderReportRequest.execute({ data: { saleId: currentSale.id, format: 'Excel' } })
                    },
                    {
                        label: 'CSV',
                        icon: 'fas fa-file-csv',
                        action: async () => lotPurchaseOrderReportRequest.execute({ data: { saleId: currentSale.id, format: 'Csv' } })
                    }
                ],
                reportIcon: 'fas fa-list',
                reportCategory: 'Lots'
            },
            {
                reportName: 'Outstanding Credits Report',
                reportDescription: 'Report for showing outstanding credits.',
                reportDownloadAction: async () => outstandingCreditsReportRequest.execute({
                    data: { format: 'Pdf' }
                }),
                reportDownloadFormats: [
                    {
                        label: 'PDF',
                        icon: 'fas fa-file-pdf',
                        action: async () => outstandingCreditsReportRequest.execute({ data: { format: 'Pdf' } })
                    },
                    {
                        label: 'Excel',
                        icon: 'fas fa-file-excel',
                        action: async () => outstandingCreditsReportRequest.execute({ data: { format: 'Excel' } })
                    },
                    {
                        label: 'CSV',
                        icon: 'fas fa-file-csv',
                        action: async () => outstandingCreditsReportRequest.execute({ data: { format: 'Csv' } })
                    }
                ],
                reportIcon: 'fas fa-dollar-sign',
                reportCategory: 'Financial'
            },
            {
                reportName: 'Unpaid Invoices',
                reportDescription: 'This report contains a list of the unpaid invoices for the current sale.',
                reportDownloadAction: async () => unpaidInvoicesReportRequest.execute({
                    data: { saleId: currentSale.id, format: 'Pdf' }
                }),
                reportDownloadFormats: [
                    {
                        label: 'PDF',
                        icon: 'fas fa-file-pdf',
                        action: async () => unpaidInvoicesReportRequest.execute({ data: { saleId: currentSale.id, format: 'Pdf' } })
                    },
                    {
                        label: 'Excel',
                        icon: 'fas fa-file-excel',
                        action: async () => unpaidInvoicesReportRequest.execute({ data: { saleId: currentSale.id, format: 'Excel' } })
                    },
                    {
                        label: 'CSV',
                        icon: 'fas fa-file-csv',
                        action: async () => unpaidInvoicesReportRequest.execute({ data: { saleId: currentSale.id, format: 'Csv' } })
                    }
                ],
                reportIcon: 'fas fa-file-invoice-dollar',
                reportCategory: 'Financial'
            }
        ]) as SaleReport[]
    });
}
